<script setup>
// BUILDER-07 (#3c): per-conversation context variables. Declarations live in
// config.context_variables; values live in each conversation's custom_attributes.
// Tools reference them as {{name}} (URL/headers), and o publish traduz o token em
// vínculo declarado — é isso que a declaração faz, e é só isso.
//
// O toggle `agent_writable` saiu daqui em 2026-09-08. Ele prometia que a variável
// ganharia uma tool `set_variable` na próxima publicação; essa tool existiu no bridge
// n8n e não sobreviveu à migração para o runtime PowerAgents — não está no catálogo
// nem no runtime, e o publish nunca a sintetizou. Um interruptor que não liga nada é
// pior que a ausência dele: quem o ligava passava a contar com uma escrita que nunca
// acontecia. Quem precisa escrever variável de conversa usa a API de custom attributes
// (`POST /conversations/:id/merge_custom_attributes`), que faz merge server-side e já
// aceita o token do bot.
// Vive dentro da aba Tools como sub-aba — variavel so existe para alimentar tool.
//
// FLOW-13 (SPEC S1; PLAN G19): a linha do topo manda a chave de API para a sub-aba Segredos, que vale
// para todos os agentes. O valor posto aqui viaja na conversa e aparece para quem a lê; o do cofre vai
// cifrado e não volta.
import { ref, inject } from 'vue';
import { useI18n, I18nT } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import powerAgentsApi from 'dashboard/api/powerAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { BUILTINS, NAME_RE, varToken } from '../variableTokens';

const props = defineProps({
  agent: { type: Object, required: true },
});

const emit = defineEmits(['irParaSegredos']);

const { t } = useI18n();
const ctx = inject('powerAgentContext');

const TIMEZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Fortaleza',
  'America/New_York',
  'Europe/Lisbon',
  'UTC',
];

const variables = ref(
  (props.agent.config?.context_variables || []).map(v => ({ ...v }))
);
const timezone = ref(
  props.agent.config?.context_timezone || 'America/Sao_Paulo'
);
const newName = ref('');
const nameError = ref('');
const saving = ref(false);
const copied = ref('');

async function copyToken(name) {
  try {
    await navigator.clipboard.writeText(varToken(name));
    copied.value = name;
    setTimeout(() => {
      if (copied.value === name) copied.value = '';
    }, 1200);
  } catch (e) {
    useAlert(t('POWER_AGENTS.SETTINGS.COPY_FAILED'));
  }
}

function addVariable() {
  const name = newName.value.trim().toLowerCase();
  if (!NAME_RE.test(name)) {
    nameError.value = t('POWER_AGENTS.VARIABLES.NAME_INVALID');
    return;
  }
  if (BUILTINS.includes(name) || variables.value.some(v => v.name === name)) {
    nameError.value = t('POWER_AGENTS.VARIABLES.NAME_TAKEN');
    return;
  }
  nameError.value = '';
  variables.value.push({
    name,
    default: '',
    visible_to_agent: true,
  });
  newName.value = '';
}

function removeVariable(index) {
  variables.value.splice(index, 1);
}

async function save() {
  saving.value = true;
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        config: {
          ...(props.agent.config || {}),
          context_variables: variables.value,
          context_timezone: timezone.value,
        },
      }
    );
    ctx.onSectionSaved(updated);
    // Declarar variável muda o que o publish traduz em vínculo: é mudança estrutural.
    ctx.markPending();
    useAlert(t('POWER_AGENTS.SAVED'));
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col gap-5">
    <div
      class="flex items-start gap-2.5 px-3 py-2.5 border rounded-lg border-n-weak"
      data-linha-dos-segredos
    >
      <span
        class="i-lucide-key-round size-4 mt-0.5 shrink-0 text-n-slate-11"
        aria-hidden="true"
      />
      <I18nT
        keypath="POWER_AGENTS.VARIABLES.SECRETS_HINT"
        tag="p"
        class="mb-0 text-sm text-n-slate-12"
      >
        <template #segredos>
          <button
            type="button"
            class="p-0 font-medium align-baseline bg-transparent text-n-brand hover:underline focus-visible:outline-2 focus-visible:outline-n-brand"
            data-ir-para-segredos
            @click="emit('irParaSegredos')"
          >
            {{ t('POWER_AGENTS.VARIABLES.SECRETS_LINK') }}
          </button>
        </template>
      </I18nT>
    </div>

    <div class="flex items-start justify-between gap-4">
      <p class="text-sm text-n-slate-11">
        {{ t('POWER_AGENTS.VARIABLES.DESCRIPTION') }}
      </p>
      <Button
        :label="t('POWER_AGENTS.SAVE')"
        size="sm"
        :is-loading="saving"
        class="shrink-0"
        @click="save"
      />
    </div>

    <!-- Prontas do sistema: clicar copia o token -->
    <div class="p-4 border rounded-xl border-n-weak bg-n-alpha-1">
      <div class="flex items-center gap-2 mb-3">
        <i class="text-sm i-lucide-sparkles text-n-slate-10" />
        <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
          {{ t('POWER_AGENTS.VARIABLES.BUILTINS') }}
        </p>
      </div>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="name in BUILTINS"
          :key="name"
          class="group flex items-center gap-1.5 px-2 py-1 font-mono text-xs transition-colors border rounded-md border-n-weak bg-n-background text-n-slate-11 hover:border-n-brand hover:text-n-slate-12"
          :title="t('POWER_AGENTS.SETTINGS.COPY')"
          @click="copyToken(name)"
        >
          {{ varToken(name) }}
          <i
            class="text-xs opacity-0 group-hover:opacity-100"
            :class="
              copied === name
                ? 'i-lucide-check text-n-teal-10 opacity-100'
                : 'i-lucide-copy'
            "
          />
        </button>
      </div>

      <div class="flex items-center gap-3 pt-3 mt-3 border-t border-n-weak">
        <label class="text-xs shrink-0 text-n-slate-11">
          {{ t('POWER_AGENTS.VARIABLES.TIMEZONE') }}
        </label>
        <select
          v-model="timezone"
          class="px-2 py-1 text-xs border rounded-md outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
        >
          <option v-for="tz in TIMEZONES" :key="tz" :value="tz">
            {{ tz }}
          </option>
        </select>
      </div>
    </div>

    <!-- Declaradas pelo agente -->
    <div class="flex flex-col gap-2">
      <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
        {{ t('POWER_AGENTS.VARIABLES.DECLARED') }}
      </p>

      <div
        v-if="!variables.length"
        class="flex flex-col items-center gap-1 px-4 py-6 text-center border border-dashed rounded-xl border-n-weak"
      >
        <i class="text-lg i-lucide-braces text-n-slate-9" />
        <p class="text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.VARIABLES.EMPTY') }}
        </p>
      </div>

      <template v-else>
        <div
          v-for="(variable, i) in variables"
          :key="variable.name"
          class="flex flex-col gap-3 p-3 transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:border-n-slate-6"
        >
          <div class="flex items-center gap-2">
            <code
              class="px-2 py-0.5 font-mono text-xs rounded-md bg-n-brand/10 text-n-brand"
            >
              {{ varToken(variable.name) }}
            </code>
            <button
              class="p-1 ml-auto rounded text-n-slate-9 hover:text-n-ruby-10 hover:bg-n-alpha-2"
              :title="t('POWER_AGENTS.VARIABLES.REMOVE')"
              @click="removeVariable(i)"
            >
              <i class="text-sm i-lucide-trash-2" />
            </button>
          </div>

          <input
            v-model="variable.default"
            :placeholder="t('POWER_AGENTS.VARIABLES.DEFAULT')"
            class="w-full px-2.5 py-1.5 text-xs border rounded-md outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />

          <div class="flex flex-wrap gap-4">
            <label class="flex items-center gap-2 text-xs text-n-slate-11">
              <Switch v-model="variable.visible_to_agent" />
              {{ t('POWER_AGENTS.VARIABLES.VISIBLE') }}
            </label>
          </div>
        </div>
      </template>

      <div class="flex items-start gap-2 mt-1">
        <div class="flex-1">
          <input
            v-model="newName"
            :placeholder="t('POWER_AGENTS.VARIABLES.NAME_PLACEHOLDER')"
            class="w-full px-2.5 py-1.5 font-mono text-xs border rounded-md outline-none border-n-weak bg-n-background focus:border-n-brand"
            @keydown.enter="addVariable"
          />
          <p v-if="nameError" class="mt-1 text-xs text-n-ruby-10">
            {{ nameError }}
          </p>
        </div>
        <Button
          :label="t('POWER_AGENTS.VARIABLES.ADD')"
          icon="i-lucide-plus"
          slate
          faded
          sm
          @click="addVariable"
        />
      </div>
    </div>

    <p class="text-xs text-n-slate-10">
      {{ t('POWER_AGENTS.VARIABLES.USAGE_HINT') }}
    </p>
  </div>
</template>
